import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { coscoOnhireApi, type CoscoOnhireRequest } from '../services/coscoOnhireApi';

/**
 * useCoscoOnhireFlow (B5)
 *
 * Drives the COSCO on-hire submission flow:
 *   1. start(taskId) → backend INSERTs a 'created' row + returns prefill
 *   2. forwards EMODAL_… style message to extension which opens Freshdesk
 *   3. extension watches the URL; on /tickets/<id> redirect it PUTs
 *      submitted to backend (no socket — we poll list on user request)
 *
 * The hook also exposes the latest in-flight / submitted request for the
 * given task so the button can render the correct state.
 */

const EXT_ID = import.meta.env.VITE_CHROME_EXT_ID || 'nniodjlikchoomnhdbcnlfncdcpodbib';

export interface UseCoscoOnhireFlowOptions {
  taskId?: number | null;
}

function sendCoscoOnhireStart(payload: {
  requestId: number;
  url: string;
  prefill: unknown;
  userJwt: string;
}): Promise<{ ok: boolean; error?: string }> {
  return new Promise((resolve) => {
    if (!window.chrome?.runtime?.sendMessage) {
      resolve({ ok: false, error: 'EAloom extension not available' });
      return;
    }
    try {
      chrome.runtime.sendMessage(
        EXT_ID,
        { type: 'COSCO_ONHIRE_START', ...payload },
        (resp: { ok?: boolean; error?: string } | undefined) => {
          if (chrome.runtime.lastError) {
            resolve({ ok: false, error: chrome.runtime.lastError.message });
          } else {
            resolve({ ok: !!resp?.ok, error: resp?.error });
          }
        }
      );
    } catch (e) {
      resolve({ ok: false, error: (e as Error).message });
    }
  });
}

export function useCoscoOnhireFlow({ taskId }: UseCoscoOnhireFlowOptions = {}) {
  const [requests, setRequests] = useState<CoscoOnhireRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!taskId) { setRequests([]); return; }
    const seq = ++requestIdRef.current;
    setLoading(true);
    try {
      const data = await coscoOnhireApi.listForTask(taskId);
      if (seq === requestIdRef.current) setRequests(data);
    } catch (e) {
      if (seq === requestIdRef.current) {
        console.error('[cosco] list failed:', (e as Error).message);
      }
    } finally {
      if (seq === requestIdRef.current) setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { void refresh(); }, [refresh]);

  // The latest request for this task — drives button state.
  // Not by submitted_at; created_at is reliable across all states.
  const latest = requests[0] || null;

  // Backend doesn't push status changes via socket, so refresh on
  // visibility/window focus to pick up extension-driven submissions
  // without making the user click anything.
  useEffect(() => {
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);

  const startOnhire = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (!taskId) return { ok: false, error: 'No task selected' };
    const userJwt = localStorage.getItem('token');
    if (!userJwt) return { ok: false, error: 'Not signed in' };
    if (!window.chrome?.runtime?.sendMessage) {
      return { ok: false, error: 'EAloom extension not available' };
    }

    let session;
    try {
      session = await coscoOnhireApi.start(taskId);
    } catch (e) {
      const msg = (e as Error).message || 'Failed to create COSCO on-hire request';
      toast.error(msg);
      return { ok: false, error: msg };
    }

    toast.loading('Opening COSCO ticket form…', { id: `cosco-${session.requestId}`, duration: 4000 });

    const handoff = await sendCoscoOnhireStart({
      requestId: session.requestId,
      url: session.url,
      prefill: session.prefill,
      userJwt,
    });
    if (!handoff.ok) {
      toast.dismiss(`cosco-${session.requestId}`);
      toast.error(handoff.error || 'Extension failed to open COSCO');
      // Best-effort: abandon the row server-side so the next attempt is clean.
      void coscoOnhireApi.markAbandoned(session.requestId).catch(() => {});
      return { ok: false, error: handoff.error };
    }
    void refresh();
    return { ok: true };
  }, [taskId, refresh]);

  const completeOnhire = useCallback(async (id: number, notes?: string) => {
    try {
      const updated = await coscoOnhireApi.markCompleted(id, notes);
      setRequests((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } catch (e) {
      toast.error((e as Error).message || 'Failed to mark completed');
    }
  }, []);

  const abandonOnhire = useCallback(async (id: number) => {
    try {
      const updated = await coscoOnhireApi.markAbandoned(id);
      setRequests((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } catch (e) {
      toast.error((e as Error).message || 'Failed to abandon');
    }
  }, []);

  return {
    requests,
    latest,
    loading,
    refresh,
    startOnhire,
    completeOnhire,
    abandonOnhire,
  };
}
